import { useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { EFFORT_SCALES, type EffortScale } from "@heraion/shared";
import { SettingsPillChoice, resolveTheme, type UiTheme } from "@heraion/ui";
import { useThemePalette } from "./theme";
import { useShellProfile } from "./ThemeProfileBridge";

// Settings → Effort on the web console: the RIR/RPE scale the Logger
// speaks app-wide. Profile scalar, get-merge-PUT via useProfileDoc. The
// value is stored canonically (RIR) regardless, so flipping this is a
// display change, never a data change. Mirror of mobile EffortScalePicker.

export function EffortScreen() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const { doc, loading, error, saveField } = useShellProfile();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const styles = makeStyles(theme);

  const current = (doc?.["effortScale"] as EffortScale | undefined) ?? "rir";

  const onPick = async (next: EffortScale) => {
    if (saving || current === next) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveField("effortScale", next);
    } catch {
      setSaveError("api_error_save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      <SettingsPillChoice<EffortScale>
        theme={theme}
        label={t("mobile.effortSettings.scaleLabel", { defaultValue: "Effort scale" })}
        help={t("mobile.effortSettings.scaleHelp", {
          defaultValue:
            "Which scale the Logger uses. RIR = reps left in the tank; RPE = perceived exertion. RPE = 10 − RIR, so they are the same effort on two scales.",
        })}
        options={EFFORT_SCALES.map((scale) => ({ value: scale, label: scale.toUpperCase() }))}
        value={current}
        onPick={(s) => void onPick(s)}
        saving={saving}
        error={saveError ?? error}
      />
    </ScrollView>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    body: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s3 },
    center: { flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: p.bg },
  });
}
